import { useId, useState, type FormEvent, type ReactNode } from 'react'
import type { ReferAs } from '../../../shared/tree'
import { Choice } from '@/components/Choice'
import { DatePicker } from '@/components/DatePicker'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { MemorialFields, PersonFields } from '@/lib/tree'

const blank: PersonFields = { name: '', referAs: null, living: true, born: null, died: null, note: '' }

type Fields = PersonFields & Partial<MemorialFields>

// A person's details, for adding someone new or editing their page. Only the name is needed; dates can be as rough
// as the family knows them. `aside` shows under the name, so it can mention whoever is being typed in. A pet gets the
// same details, in words that fit a pet. Editing the page of someone who has passed also asks for their memorial
// page's words: a line to remember them by, their story and where they rest (`focus` starts on the story).
export function PersonForm({
  initial = blank,
  pet = false,
  withNote = false,
  focus,
  label,
  pending,
  error,
  aside,
  onSave,
}: {
  initial?: Fields
  pet?: boolean
  withNote?: boolean
  focus?: 'story'
  label: string
  pending: boolean
  error: Error | null
  aside?: (fields: PersonFields) => ReactNode
  onSave: (fields: Fields) => void
}) {
  const id = useId()
  const [f, setF] = useState<Fields>(initial)
  const set = (patch: Partial<Fields>) => setF((x) => ({ ...x, ...patch }))
  const memorial = withNote && !f.living

  function save(e: FormEvent) {
    e.preventDefault()
    onSave({
      ...f,
      name: f.name.trim(),
      died: f.living ? null : f.died,
      note: f.note.trim(),
      ...(f.epitaph !== undefined && { epitaph: f.epitaph.trim() }),
      ...(f.story !== undefined && { story: f.story.trim() }),
      ...(f.restingPlace !== undefined && { restingPlace: f.restingPlace.trim() }),
    })
  }

  return (
    <form onSubmit={save} className="grid gap-5">
      <div className="grid gap-2">
        <Label htmlFor={`${id}-name`}>Name</Label>
        <Input
          id={`${id}-name`}
          required
          maxLength={80}
          autoComplete="off"
          value={f.name}
          onChange={(e) => set({ name: e.target.value })}
        />
        {aside?.(f)}
      </div>

      <Choice
        legend="How do you refer to them?"
        hint={pet ? undefined : 'For words like mother or son. Leave it and they’re parent or child.'}
        value={f.referAs ?? 'they'}
        options={[
          ['she', 'She'],
          ['he', 'He'],
          ['they', 'They'],
        ]}
        onChange={(v) => set({ referAs: v === 'they' ? null : (v as ReferAs) })}
      />

      <Choice
        legend="Are they still with us?"
        value={f.living ? 'living' : 'passed'}
        options={[
          ['living', 'Living'],
          ['passed', 'Passed away'],
        ]}
        onChange={(v) => set({ living: v === 'living' })}
      />

      <div className="grid gap-3">
        <div className="grid gap-2">
          <Label htmlFor={`${id}-born`}>Born</Label>
          <DatePicker id={`${id}-born`} value={f.born} onChange={(born) => set({ born })} />
        </div>
        {!f.living && (
          <div className="grid gap-2">
            <Label htmlFor={`${id}-died`}>Died</Label>
            <DatePicker id={`${id}-died`} value={f.died} onChange={(died) => set({ died })} />
          </div>
        )}
        <p className="text-sub text-muted-foreground">A year is enough if that’s all anyone knows.</p>
      </div>

      {withNote && (
        <div className="grid gap-2">
          <Label htmlFor={`${id}-note`}>A note</Label>
          <Textarea id={`${id}-note`} maxLength={5000} value={f.note} onChange={(e) => set({ note: e.target.value })} />
        </div>
      )}

      {memorial && (
        <>
          <div className="grid gap-2">
            <Label htmlFor={`${id}-epitaph`}>A line to remember them by</Label>
            <Input
              id={`${id}-epitaph`}
              maxLength={200}
              autoComplete="off"
              value={f.epitaph ?? ''}
              onChange={(e) => set({ epitaph: e.target.value })}
            />
            <p className="text-sub text-muted-foreground">It shows under their name.</p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`${id}-story`}>{pet ? 'Their story' : 'Their life story'}</Label>
            <Textarea
              id={`${id}-story`}
              autoFocus={focus === 'story'}
              maxLength={20000}
              rows={8}
              value={f.story ?? ''}
              onChange={(e) => set({ story: e.target.value })}
            />
            <p className="text-sub text-muted-foreground">
              A few lines is plenty. Leave a blank line between paragraphs.
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`${id}-rests`}>Resting place</Label>
            <Input
              id={`${id}-rests`}
              maxLength={200}
              autoComplete="off"
              value={f.restingPlace ?? ''}
              onChange={(e) => set({ restingPlace: e.target.value })}
            />
          </div>
        </>
      )}

      <FormError error={error} />
      <Button type="submit" disabled={pending} className="self-start">
        {label}
      </Button>
    </form>
  )
}
